import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { VideoPlayer as VideoPlayerComponent } from '../components/common';
import {
  FiTarget,
  FiUsers,
  FiCheckCircle,
  FiBookOpen,
  FiGlobe,
  FiClock
} from 'react-icons/fi';
import {
  FaMedal,
  FaHandshake,
  FaHandsHelping,
  FaUserTie,
  FaTools
} from 'react-icons/fa';
import {
  MdOutlineVerified
} from 'react-icons/md';

// Enhanced Hero Section
const HeroSection = styled.section`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: white;
  padding: 8rem 0 6rem;
  position: relative;
  overflow: hidden;

  /* Background image with overlay */
  background-image: 
    linear-gradient(
      135deg, 
      rgba(1, 103, 95, 0.9), 
      rgba(0, 82, 76, 0.85)
    ),
    url('/Background Image 1.webp');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000"><polygon fill="rgba(255,255,255,0.05)" points="0,1000 1000,0 1000,1000"/></svg>');
    background-size: cover;
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 7rem 0 5rem;
    background-position: center 30%;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 6rem 0 4rem;
    background-position: center 25%;
  }

  @media (max-width: 768px) {
    padding: 6rem 0 4rem;
    background-position: center 20%;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.1;
  color: white;

  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  font-weight: 400;
  margin-bottom: 2rem;
  line-height: 1.4;
  opacity: 0.95;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  color: white;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

// Enhanced Content Sections
const ContentSection = styled.section`
  padding: 6rem 0;
  position: relative;

  &:nth-child(even) {
    background: ${props => props.theme.colors.background};
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  &.reverse {
    direction: rtl;

    > * {
      direction: ltr;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;

    &.reverse {
      direction: ltr;
    }
  }
`;

const ContentText = styled.div`
  h2 {
    font-size: 2.8rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1.5rem;
    font-weight: 700;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 2.2rem;
    }
  }

  h3 {
    color: ${props => props.theme.colors.secondary};
    font-size: 1.1rem;
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    font-weight: 600;
  }

  p {
    font-size: 1.15rem;
    line-height: 1.8;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1.5rem;
  }
`;

const ContentImage = styled.div`
  position: relative;

  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 20px;
    box-shadow: ${props => props.theme.shadows.xl};
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.02);
  }

  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: 20px;
    bottom: 20px;
    background: linear-gradient(135deg, ${props => props.theme.colors.primary}20, ${props => props.theme.colors.secondary}20);
    border-radius: 20px;
    z-index: -1;
  }
`;

/* Enhanced Vision/Mission Section */
const VisionMissionSection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, ${props => props.theme.colors.background} 0%, ${props => props.theme.colors.surface} 100%);
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: 3rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
    font-weight: 700;
  }

  p {
    font-size: 1.2rem;
    color: ${props => props.theme.colors.textSecondary};
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const VisionMissionCard = styled.div`
  background: white;
  padding: 3rem;
  border-radius: 25px;
  box-shadow: ${props => props.theme.shadows.lg};
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.xl};
  }

  h3 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
  }

  p {
    color: ${props => props.theme.colors.text};
    line-height: 1.7;
    margin-bottom: 2rem;
    font-size: 1.1rem;
  }

  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: 15px;
  }
`;

/* Enhanced Values Section with Flat Icons */
const ValuesSection = styled.section`
  padding: 6rem 0;
  background: ${props => props.theme.colors.background};
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  margin-top: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ValueCard = styled.div`
  background: white;
  padding: 3rem 2rem;
  border-radius: 25px;
  box-shadow: ${props => props.theme.shadows.md};
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.color || props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const ValueIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #32CD32, #01675f);
  color: white;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  font-size: 2rem;
  box-shadow: ${props => props.theme.shadows.lg};
  transition: transform 0.3s ease;

  ${ValueCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }
`;

const ValueTitle = styled.h4`
  color: ${props => props.theme.colors.primary};
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const ValueDescription = styled.p`
  color: ${props => props.theme.colors.text};
  font-size: 1rem;
  line-height: 1.7;
`;

const ProcessSection = styled.section`
  padding: 6rem 0;
  background: white;
`;

// Stats Section
const StatsSection = styled.section`
  padding: 4rem 0;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  color: white;
  text-align: center;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 3rem;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
`;

const StatItem = styled.div`
  h3 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: white;
  }

  p {
    font-size: 1.1rem;
    opacity: 0.9;
    color: white;
  }
`;

const ProcessSteps = styled.div`
  max-width: 800px;
  margin: 0 auto;

  h2 {
    font-size: 2.8rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1.5rem;
    font-weight: 700;
    text-align: center;
  }

  p {
    font-size: 1.15rem;
    line-height: 1.8;
    color: ${props => props.theme.colors.textSecondary};
    margin-bottom: 3rem;
    text-align: center;
  }
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 1.5rem;
  padding: 2rem 1.5rem;
  background: ${props => props.theme.colors.background};
  border-radius: 15px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const StepIcon = styled.div`
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
  font-size: 1.5rem;
  box-shadow: ${props => props.theme.shadows.md};
`;

const StepContent = styled.div`
  h4 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
  }

  p {
    color: ${props => props.theme.colors.text};
    font-size: 1rem;
    line-height: 1.6;
    margin: 0;
  }
`;

const AboutPage = () => {
  const [stats, setStats] = useState({
    qualifications: 0,
    sectors: 0,
    successRate: 0,
    satisfaction: 0
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsSectionRef = React.useRef(null);

  const targetStats = {
    qualifications: 5000,
    sectors: 50,
    successRate: 98,
    satisfaction: 4.9
  };

  const startCountingAnimation = () => {
    if (hasAnimated) return;
    
    setHasAnimated(true);
    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepDuration = duration / steps;

    const timer = setInterval(() => {
      setStats(prevStats => {
        const newStats = { ...prevStats };
        let allComplete = true;

        Object.keys(targetStats).forEach(key => {
          if (newStats[key] < targetStats[key]) {
            const increment = targetStats[key] / steps;
            newStats[key] = Math.min(newStats[key] + increment, targetStats[key]);
            allComplete = false;
          }
        });

        if (allComplete) {
          clearInterval(timer);
        }

        return newStats;
      });
    }, stepDuration);

    return () => clearInterval(timer);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startCountingAnimation();
        }
      },
      { threshold: 0.2 } // Start animation when 20% of the section is visible
    );

    if (statsSectionRef.current) {
      observer.observe(statsSectionRef.current);
    }

    return () => {
      if (statsSectionRef.current) {
        observer.unobserve(statsSectionRef.current);
      }
    };
  }, []);

  const values = [
    {
      icon: <FaMedal />,
      title: 'Excellence',
      description: 'We maintain the highest standards in all our assessments and services, ensuring your qualification meets industry requirements.',
      color: '#FFD700'
    },
    {
      icon: <FaHandshake />,
      title: 'Trust',
      description: 'We build lasting relationships with our clients based on transparency, honesty, and reliable service delivery.',
      color: '#4CAF50'
    },
    {
      icon: <FaHandsHelping />,
      title: 'Support',
      description: 'We provide comprehensive support throughout your RPL journey, ensuring you feel confident and informed at every step.',
      color: '#2196F3'
    }
  ];

  const processSteps = [
    {
      icon: <FiUsers />,
      title: 'Initial Consultation',
      description: 'Free skills assessment and qualification matching'
    },
    {
      icon: <FiBookOpen />,
      title: 'Portfolio Preparation',
      description: 'Document collection and evidence compilation'
    },
    {
      icon: <FaUserTie />,
      title: 'Professional Assessment',
      description: 'Qualified assessors review your skills and experience'
    },
    {
      icon: <FiCheckCircle />,
      title: 'Gap Analysis',
      description: 'Identify any additional requirements or training needed'
    },
    {
      icon: <FaTools />,
      title: 'Additional Training',
      description: 'Complete any required gap training if necessary'
    },
    {
      icon: <MdOutlineVerified />,
      title: 'Qualification Award',
      description: 'Receive your nationally recognised qualification'
    }
  ];

  return (
    <>
      {/* Enhanced Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>About RPL Support</HeroTitle>
          <HeroSubtitle>
            Transforming Skills Into Recognised Qualifications Across Australia
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      {/* Enhanced About Section */}
      <ContentSection>
        <Container>
          <ContentGrid>
            <ContentImage>
              <img
                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&h=600&fit=crop&crop=center"
                alt="Professional team collaboration"
              />
            </ContentImage>
            <ContentText>
              <h3>Our Story</h3>
              <h2>Turn Your Experience Into Recognised Qualifications</h2>
              <p>
                We understand that life experience and on-the-job training are just as valuable as formal education.
                Our Recognition of Prior Learning (RPL) services help you transform your existing skills and knowledge
                into nationally recognised qualifications.
              </p>
              <p>
                Whether you've gained expertise through work experience, volunteering, or informal training,
                we can help you get the formal recognition you deserve. Our expert assessors work with you
                to identify your skills and match them to appropriate qualifications.
              </p>
            </ContentText>
          </ContentGrid>

          <ContentGrid className="reverse">
            <ContentImage>
              <img
                src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=600&fit=crop&crop=center"
                alt="Construction professionals"
              />
            </ContentImage>
            <ContentText>
              <h2>Industry-Leading Recognition Across All Sectors</h2>
              <p>
                From construction and healthcare to business administration and information technology,
                we provide RPL services across a comprehensive range of industries. Our experienced team
                understands the specific requirements and standards for each sector.
              </p>
              <p>
                We partner with leading Registered Training Organisations (RTOs) to ensure your qualifications
                are nationally recognised and industry-relevant. This means your new qualification will be
                valued by employers across Australia and internationally.
              </p>
            </ContentText>
          </ContentGrid>
        </Container>
      </ContentSection>

      {/* Stats Section */}
      <StatsSection ref={statsSectionRef}>
        <Container>
          <StatsGrid>
            <StatItem>
              <h3>{Math.round(stats.qualifications).toLocaleString()}+</h3>
              <p>Qualifications Awarded</p>
            </StatItem>
            <StatItem>
              <h3>{Math.round(stats.sectors)}+</h3>
              <p>Industry Sectors</p>
            </StatItem>
            <StatItem>
              <h3>{Math.round(stats.successRate)}%</h3>
              <p>Success Rate</p>
            </StatItem>
            <StatItem>
              <h3>{stats.satisfaction.toFixed(1)}/5</h3>
              <p>Client Satisfaction</p>
            </StatItem>
          </StatsGrid>
        </Container>
      </StatsSection>

      {/* Enhanced Vision/Mission Section */}
      <VisionMissionSection>
        <Container>
          <SectionHeader>
            <h2>Our Vision & Mission</h2>
            <p>
              We're dedicated to bridging the gap between experience and formal recognition,
              empowering professionals across Australia to achieve their career aspirations.
            </p>
          </SectionHeader>
          <CardsGrid>
            <VisionMissionCard>
              <FiTarget style={{ fontSize: '3rem', color: '#01675f', marginBottom: '1.5rem' }} />
              <h3>Our Vision</h3>
              <p>
                To be Australia's most trusted provider of Recognition of Prior Learning services,
                empowering every professional to achieve their full potential through formal recognition
                of their skills and experience.
              </p>
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=200&fit=crop&crop=center"
                alt="Business vision"
              />
            </VisionMissionCard>
            <VisionMissionCard>
              <FiGlobe style={{ fontSize: '3rem', color: '#01675f', marginBottom: '1.5rem' }} />
              <h3>Our Mission</h3>
              <p>
                To provide accessible, professional, and comprehensive RPL services that help
                working professionals gain the qualifications they deserve, advancing their
                careers and strengthening Australia's skilled workforce.
              </p>
              <img
                src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400&h=200&fit=crop&crop=center"
                alt="Professional mission"
              />
            </VisionMissionCard>
          </CardsGrid>
        </Container>
      </VisionMissionSection>

      {/* Enhanced Process Section */}
      <ProcessSection>
        <Container>
          <ProcessSteps>
            <h2>Your RPL Journey</h2>
            <p>
              Our streamlined process is designed to make gaining your qualification as simple as possible.
              We guide you through each step with expert support and clear communication.
            </p>
            <StepsGrid>
              {processSteps.map((step, index) => (
                <StepItem key={index}>
                  <StepIcon>
                    {step.icon}
                  </StepIcon>
                  <StepContent>
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                  </StepContent>
                </StepItem>
              ))}
            </StepsGrid>
            <p style={{fontSize: '1rem', color: '#666', marginTop: '2rem', fontStyle: 'italic', textAlign: 'center'}}>
              <FiClock style={{display: 'inline-block', marginRight: '0.5rem'}} />
              Most clients complete the RPL process within 4-8 weeks, depending on qualification complexity.
            </p>
          </ProcessSteps>
        </Container>
      </ProcessSection>

      {/* Enhanced Values Section with Flat Icons */}
      <ValuesSection>
        <Container>
          <SectionHeader>
            <h2>Our Core Values</h2>
            <p>
              These principles guide every decision we make and every interaction we have with our clients.
            </p>
          </SectionHeader>
          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard key={index}>
                <ValueIcon color={value.color}>
                  {value.icon}
                </ValueIcon>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueCard>
            ))}
          </ValuesGrid>
        </Container>
      </ValuesSection>

      {/* Video Section */}
      <VideoPlayerComponent
        videoSrc="/video2.mp4"
        title="About RPL Support"
        subtitle="Learn more about our mission and how we help professionals achieve their career goals through Recognition of Prior Learning."
        description="Discover our story, values, and commitment to making quality education accessible to everyone through our innovative RPL approach."
      />
    </>
  );
};

export default AboutPage;